import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Languages, Save, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const FIELDS = [
  { key: 'bannerInvite', label: 'Заголовок (приглашение)', rows: 2 },
  { key: 'bannerCountdown', label: 'Строка с таймером и баллами', rows: 3 },
  { key: 'bannerHints', label: 'Доп. строка про подсказки', rows: 2 },
  { key: 'bannerCTA', label: 'Кнопка призыва к действию', rows: 1 },
];

const LOCALE_NAMES = {
  ru: 'Русский', en: 'English', es: 'Español', de: 'Deutsch',
  fr: 'Français', pt: 'Português', zh: '中文',
};

/**
 * Admin sub-section for editing the FOMO referral banner shown to visitors
 * arriving through /game/ref/:slug.  Russian only — saving auto-translates
 * to all other supported locales via the Yandex Translate API.
 */
export default function AdminReferralBannerEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ru, setRu] = useState({ bannerInvite: '', bannerCountdown: '', bannerHints: '', bannerCTA: '' });
  const [translations, setTranslations] = useState({});
  const [placeholders, setPlaceholders] = useState([]);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/admin/referral-banner/template`, { headers: getAuthHeader() })
      .then(r => {
        const data = r.data || {};
        const ruDoc = data.ru || {};
        setRu({
          bannerInvite: ruDoc.bannerInvite || '',
          bannerCountdown: ruDoc.bannerCountdown || '',
          bannerHints: ruDoc.bannerHints || '',
          bannerCTA: ruDoc.bannerCTA || '',
        });
        setTranslations(data.translations || {});
        setPlaceholders(data.placeholders || []);
      })
      .catch(() => toast.error('Не удалось загрузить текст баннера'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!ru.bannerInvite.trim() || !ru.bannerCountdown.trim() || !ru.bannerCTA.trim()) {
      toast.error('Заголовок, строка со счётчиком и кнопка обязательны');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/api/admin/referral-banner/template`,
        ru,
        { headers: getAuthHeader() },
      );
      const ok = r.data?.translated_ok || [];
      const fail = r.data?.translated_failed || [];
      if (fail.length === 0) {
        toast.success(`Сохранено, автоперевод на: ${ok.join(', ').toUpperCase()}`);
      } else {
        toast.warning(`Сохранено. Не переведено: ${fail.join(', ').toUpperCase()}`);
      }
      // Reload to see the freshly translated copies
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '0.75rem', color: '#666' }}>Загрузка…</div>;

  return (
    <div data-testid="admin-referral-banner-editor" style={{ marginTop: '2rem' }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Languages size={16} /> Текст FOMO-баннера для приглашённых
      </h3>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
        Редактируется только на русском. При сохранении автоматически переводится на
        все остальные языки через Yandex Translate. Плейсхолдеры (например, <code>{'{name}'}</code>)
        сохраняются неизменными.
      </p>

      {placeholders.length > 0 && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '8px 12px', marginBottom: 14, fontSize: 12.5, color: '#475569',
        }}>
          <b style={{ marginRight: 4 }}>Доступные плейсхолдеры:</b>
          {placeholders.map(p => (
            <span key={p.key} style={{
              display: 'inline-block', margin: '4px 6px 0 0',
              padding: '2px 8px', borderRadius: 6, background: 'white',
              border: '1px solid #cbd5e1', fontFamily: 'monospace',
            }} title={p.label}>{p.key}</span>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {FIELDS.map(f => (
          <div key={f.key}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              {f.label}
            </label>
            {f.rows > 1 ? (
              <textarea
                value={ru[f.key]}
                onChange={e => setRu(s => ({ ...s, [f.key]: e.target.value }))}
                rows={f.rows}
                data-testid={`banner-input-${f.key}`}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db',
                  fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            ) : (
              <input
                value={ru[f.key]}
                onChange={e => setRu(s => ({ ...s, [f.key]: e.target.value }))}
                data-testid={`banner-input-${f.key}`}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db',
                  fontSize: 14,
                }}
              />
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          onClick={save}
          disabled={saving}
          data-testid="banner-save-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: saving ? '#9ca3af' : '#0a4797', color: 'white',
            fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? <RefreshCw size={14} className="spin" /> : <Save size={14} />}
          {saving ? 'Сохраняем и переводим…' : 'Сохранить + перевести на 6 языков'}
        </button>
        <button
          onClick={load}
          disabled={saving}
          style={{
            padding: '9px 14px', borderRadius: 8, border: '1px solid #d1d5db',
            background: 'white', color: '#374151', cursor: 'pointer', fontSize: 13,
          }}
        >
          Сбросить
        </button>
      </div>

      {/* Read-only translations preview */}
      {Object.keys(translations).length > 0 && (
        <div style={{ marginTop: 22 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            Текущие переводы (только чтение)
          </h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {Object.entries(translations).map(([loc, doc]) => {
              const empty = !doc || !doc.bannerInvite;
              return (
                <details key={loc} style={{
                  background: empty ? '#fef2f2' : '#f8fafc',
                  border: '1px solid ' + (empty ? '#fecaca' : '#e2e8f0'),
                  borderRadius: 8, padding: '6px 10px', fontSize: 12.5,
                }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#1f2937' }}>
                    {LOCALE_NAMES[loc] || loc.toUpperCase()}
                    {empty && <span style={{ marginLeft: 8, color: '#b91c1c' }}>— не переведено</span>}
                    {doc?.source === 'auto-translated' && (
                      <span style={{ marginLeft: 8, color: '#6b7280', fontWeight: 400 }}>(авто)</span>
                    )}
                  </summary>
                  {!empty && (
                    <div style={{ marginTop: 6, color: '#475569', lineHeight: 1.5 }}>
                      <div><b>Заголовок:</b> {doc.bannerInvite}</div>
                      <div><b>Счётчик:</b> {doc.bannerCountdown}</div>
                      <div><b>Подсказки:</b> {doc.bannerHints}</div>
                      <div><b>CTA:</b> {doc.bannerCTA}</div>
                    </div>
                  )}
                </details>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
